import { Payment, Toast, UI, Utils } from 'common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { isString } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import AccountUtil from '../utils/Util';
import { ActionType } from './ActionType.enum';
import ManagePaymentModal from './ManagePaymentModal.component';
import { NsaSurchargesMessage } from './NsaSurchargesMessage.component';
import { PaymentAccountType as PaymentMethodType } from './PaymentAccountType.enum';

const {
	WrappedDropdownWithLabel,
	WrappedRadioButton,
	WrappedButton,
	WrappedButtonType,
	SaveConfirmation,
	Spinner,
} = UI;
const { Util: PaymentUtil } = Payment;
const {
	Logger,
	Session,
	withLayoutAndModalContexts,
	UserRole,
	Analytics,
	Util,
} = Utils;
const { withToast } = Toast;
const { CREATE, EDIT } = ActionType;
const errorMessagePayment = 'Payment method required';

class PaymentSettings extends Component {
	constructor(props) {
		super(props);

		this.setPaymentFormStateDirty(false);

		const { customerId } = Session.getAccountInfo();
		this.userType = Session.getUserRole();
		const { enterpriseLocation } = this.props;

		this.state = {
			newValue: {
				postage: '',
				serviceFee: '',
				store: '',
			},
			initialValue: {
				postage: '',
				serviceFee: '',
				store: '',
			},
			paymentAccounts: [],
			paymentAccountForChargeCategory: {},
			associatePayment: {
				postage: [],
				serviceFee: [],
				store: [],
			},
			validations: {
				postage: 0,
				serviceFee: 0,
				store: 0,
			},
			saved: false,
			/**
			 * If user is a corp admin (or a region admin), there are two account data user
			 * can see, corporation account and meter account.
			 *
			 * System fetches the corporation account data when user clicks "Payment settings"
			 * tab under "Corporate Settings" (or "Region Settings" for a region admin) as
			 * user is mananging the methods for the entire corporation (or the region).
			 *
			 * System fetches the meter account data when user clicks "Payment settings" tab
			 * under "My Location Settings" or "Default Payment" through the location
			 * setting dialog as user is managing the methods for the location he or she is
			 * assigned to.
			 */
			isForCorpPaymentAccount: this.isForCorpPaymentAccount(),
			customerId: enterpriseLocation ? enterpriseLocation.CustId : customerId,
			transactionTypeForChargeCategory: {
				postage: true,
				serviceFee: true,
				store: true,
			},
			categoryUsingInvoicing: {},
			categoryUsingEps: {},
			categoryUsingCorporationPaymentAccount: {},
			categoryUsingOmas: {},
			paymentSettingsTitle: 'Payment Settings',
			clean: true,
			isLoading: false,
			loadingMessage: '',
			accountInResubmission: PaymentUtil.isInResubmitState(),
			showAuctanePay: false,
			isNsaSurchargeEnrolled: false,
			isBankPaymentMethodSelected: false,
			isSurchargesMessageDismissed: true,
			isRedirectedFromConnectBankAccount: false,
		};

		this.nameDropdowns = {
			accountFunds: 'postage',
			serviceFees: 'serviceFee',
			suppliesPurchases: 'store',
		};
	}

	componentDidMount() {
		const { isForCorpPaymentAccount } = this.state;
		const { location } = this.props;

		const { DismissNsaSurchargesMessageInPaymentSettings } =
			Session.getPreferences();
		const { isNsaSurchargeEnrolled } = Session.getDynamicSettings();
		const sessionId = Util.getQueryParamByName('sessionId');
		const redirectResult = Util.getQueryParamByName('redirectResult');

		const isRedirectedFromConnectBankAccount =
			isString(sessionId) && isString(redirectResult);

		if (isRedirectedFromConnectBankAccount) {
			Util.removeQueryParamByName(['sessionId', 'redirectResult']);
		}

		this.setState({
			isSurchargesMessageDismissed:
				DismissNsaSurchargesMessageInPaymentSettings === '1',
			isNsaSurchargeEnrolled,
			isRedirectedFromConnectBankAccount,
		});

		this.displayLoadingSpinner(true, 'Loading payment settings...');

		if (isForCorpPaymentAccount) {
			this.getCorporationPaymentAccounts();
			this.getEnterprisePaymentSettingsTitle();
		} else {
			this.getPaymentAccounts();
		}

		// check if user is redirected via /paymentsettings/add
		const isPathAddPaymentSettings =
			location && location.pathname === RoutePath.addPaymentSettings;

		if (isPathAddPaymentSettings) {
			this.addPayment();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { isRedirectedFromConnectBankAccount, isLoading } = this.state;
		const { isNsaSurchargeEnrolled } = Session.getDynamicSettings();
		if (
			!isLoading &&
			isRedirectedFromConnectBankAccount &&
			isNsaSurchargeEnrolled
		) {
			this.showBankAccountRedirectionToast();
			this.setState({ isRedirectedFromConnectBankAccount: false });
		}

		if (prevState.isNsaSurchargeEnrolled !== isNsaSurchargeEnrolled) {
			this.setState({ isNsaSurchargeEnrolled });
		}
	}

	dismissSurchargeMessage = () => {
		Session.savePreferences([
			{ Name: 'DismissNsaSurchargesMessageInPaymentSettings', Value: '1' },
		]);
		this.setState({ isSurchargesMessageDismissed: true });
	};

	/**
	 * Return `true` if system should fetch corporation payment account data.
	 * @returns {boolean}
	 */
	isForCorpPaymentAccount = () => {
		const { location, enterpriseLocation } = this.props;

		// get user rule
		const userRole = Session.getUserRole();

		// check on URL path, '/paymentsettings' or '/localpaymentsettings'
		const isPathLocationPaymentSettings =
			location && location.pathname === RoutePath.locationPaymentSettings;

		return (
			(userRole === UserRole.CorpAdmin || userRole === UserRole.RegionAdmin) &&
			!isPathLocationPaymentSettings &&
			!enterpriseLocation
		);
	};

	setPaymentFormStateDirty = isDirty => {
		const { enterpriseLocation } = this.props;

		if (!enterpriseLocation) {
			Session.setFormStateDirty(isDirty);
		}
	};

	getEnterprisePaymentSettingsTitle = () => {
		switch (this.userType) {
			case UserRole.CorpAdmin:
				this.setState({
					paymentSettingsTitle: 'Corporate Payment Settings',
				});
				break;
			case UserRole.RegionAdmin:
				this.setState({
					paymentSettingsTitle: 'Region Payment Settings',
				});
				break;
			default:
				break;
		}
	};

	getChanges = () => {
		const { newValue, initialValue } = this.state;
		return Object.keys(newValue).filter(
			key => newValue[key] !== initialValue[key]
		);
	};

	checkIfPaymentExistsMeterLevel = chargeCategoryPaymentId => {
		const { paymentAccounts } = this.state;

		return paymentAccounts.some(
			payment => payment.PaymentAccountId === chargeCategoryPaymentId
		);
	};

	checkOverriddenStatus = category => {
		const {
			categoryUsingOmas,
			categoryUsingInvoicing,
			categoryUsingCorporationPaymentAccount,
			categoryUsingEps,
		} = this.state;
		/**
		 * Returns override checks based on dropdown category
		 */
		if (category === 'store') {
			return (
				categoryUsingCorporationPaymentAccount.Store ||
				categoryUsingInvoicing.IsStoreInvoiced
			);
		} else if (category === 'serviceFee') {
			return (
				categoryUsingInvoicing.IsServiceFeeInvoiced ||
				categoryUsingCorporationPaymentAccount.Service
			);
		} else if (category === 'postage') {
			return (
				categoryUsingOmas.IsPostageEnabled ||
				categoryUsingInvoicing.IsPostageInvoiced ||
				categoryUsingCorporationPaymentAccount.Postage ||
				categoryUsingEps.EPSAccountNumber !== null
			);
		} else {
			return null;
		}
	};

	validateData = () => {
		const {
			newValue,
			validations,
			transactionTypeForChargeCategory,
			isForCorpPaymentAccount,
		} = this.state;
		const newValidations = { ...validations };
		let hasErrorField = 0;

		Object.keys(newValue).forEach((key, index, arr) => {
			/**
			 * 1.  paymentExistsMeterLevel - Checks if the payment exists.
			 * 2.  checkOverriddenStatus -   Checks if overridden by EPS / OMAS / Invoicing / Corporate Overrides.
			 */
			const paymentExistsMeterLevel = this.checkIfPaymentExistsMeterLevel(
				newValue[key]
			);
			const overrideSuccessful = !paymentExistsMeterLevel
				? this.checkOverriddenStatus(arr[index])
				: null;

			if (
				(!newValue[key] && !transactionTypeForChargeCategory[key]) ||
				(!isForCorpPaymentAccount && overrideSuccessful === false)
			) {
				newValidations[key] = -1;
				hasErrorField = +1;
			} else {
				newValidations[key] = 0;
			}
		});

		this.setState({
			validations: newValidations,
		});

		return hasErrorField > 0;
	};

	savePaymentSettings = () => {
		const { isForCorpPaymentAccount } = this.state;
		const isInvalid = this.validateData();

		if (isInvalid) {
			return;
		}

		this.displayLoadingSpinner(true, 'Updating payment settings...');
		if (isForCorpPaymentAccount) {
			this.associateCorpPaymentAccountForChargeCategory();
		} else {
			this.associatePaymentAccountForChargeCategory();
		}

		Logger.logToServer({
			action: 'click',
			ui_element: 'save_button',
			category: 'Save Changes',
			fieldsChanged: this.getChanges(),
			module: 'ui_save_payment_settings',
		});
	};

	/**
	 * For implement Edit Method link
	 */
	editPayment = paymentInfo => () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const {
			paymentAccounts,
			paymentAccountForChargeCategory,
			isForCorpPaymentAccount,
			customerId,
		} = this.state;

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'edit_card_link',
			module: 'ui_edit_payment_dialog_onshow',
		});

		showModal({
			component: ManagePaymentModal,
			data: {
				paymentInfo,
				action: EDIT,
				allAccounts: paymentAccounts,
				callback: this.callback,
				isCorpAccount: isForCorpPaymentAccount,
				customerId,
				paymentAccountForChargeCategory,
			},
		});
	};

	/**
	 * For implement Add Payment Method button
	 */
	addPayment = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const {
			paymentAccounts,
			isForCorpPaymentAccount,
			customerId,
			showAuctanePay,
		} = this.state;

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'add_payment_button',
			module: 'ui_add_payment_dialog_onshow',
		});

		showModal({
			component: ManagePaymentModal,
			data: {
				action: CREATE,
				allAccounts: paymentAccounts,
				callback: this.callback,
				isCorpAccount: isForCorpPaymentAccount,
				customerId,
				showAuctanePay,
				autoSelectPayByBank: true,
			},
		});
	};

	callback = ({ status }) => {
		const { isForCorpPaymentAccount } = this.state;

		if (status) {
			this.displayLoadingSpinner(true, 'Updating payment settings...');

			if (isForCorpPaymentAccount) {
				this.getCorporationPaymentAccounts();
			} else {
				this.getPaymentAccounts();
			}
		}
	};

	managePaymentMethods = () => {
		const { history } = this.props;

		history.push(RoutePath.paymentMethods);
	};

	getPaymentAccounts = async () => {
		const { customerId } = this.state;
		const {
			modalContext: { showError },
			flags,
		} = this.props;
		let showAuctanePay = false;

		const response = await AccountCommunication.getPaymentAccounts(customerId);

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			const data = isObject(response.data) ? response.data : {};
			const {
				PaymentAccounts = [],
				PaymentAccountForChargeCategory = {},
				ChargeCategoryUsingCorporationPaymentAccount = {},
				ChargeCategoryUsingEPS = {},
				ChargeCategoryUsingInvoicing = {},
				ChargeCategoryUsingOMAS = {},
			} = data;

			const newValue = {
				postage: PaymentAccountForChargeCategory.Postage,
				serviceFee: PaymentAccountForChargeCategory.ServiceFee,
				store: PaymentAccountForChargeCategory.Store,
			};

			const isAuctanePayAccount = PaymentAccounts.some(payment =>
				AccountUtil.hasAccountMigratedToAuctanePay(payment.PaymentAccountId)
			);

			if (isAuctanePayAccount && flags.useAuctanePay) {
				showAuctanePay = true;
			}

			const currentAccountFundsPayment = PaymentAccounts.find(
				payment =>
					payment.PaymentAccountId === PaymentAccountForChargeCategory.Postage
			);
			const isBankPaymentMethodSelected =
				currentAccountFundsPayment?.PaymentAccountType ===
				PaymentMethodType.ACH;

			this.setState({
				newValue,
				initialValue: newValue,
				paymentAccounts: PaymentAccounts,
				paymentAccountForChargeCategory: PaymentAccountForChargeCategory,
				associatePayment: this.getAssociatePayment(
					PaymentAccounts,
					PaymentAccountForChargeCategory
				),
				categoryUsingCorporationPaymentAccount:
					ChargeCategoryUsingCorporationPaymentAccount,
				categoryUsingEps: ChargeCategoryUsingEPS,
				categoryUsingInvoicing: ChargeCategoryUsingInvoicing,
				categoryUsingOmas: ChargeCategoryUsingOMAS,
				showAuctanePay,
				isBankPaymentMethodSelected,
			});
		} else {
			showError({ response });
		}
	};

	getCorporationPaymentAccounts = async () => {
		const {
			modalContext: { showError },
			flags,
		} = this.props;
		let showAuctanePay = false;

		const response = await AccountCommunication.getCorporationPaymentAccounts();

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			const data = isObject(response.data) ? response.data : {};
			const {
				PaymentAccounts = [],
				PaymentAccountForChargeCategory = {},
				ChargeCategoryUsingCorporationPaymentAccount = {},
			} = data;
			const associatePayment = this.getAssociatePayment(
				PaymentAccounts,
				PaymentAccountForChargeCategory
			);
			const newValue = {
				postage: PaymentAccountForChargeCategory.Postage,
				serviceFee: PaymentAccountForChargeCategory.ServiceFee,
				store: PaymentAccountForChargeCategory.Store,
			};

			const isAuctanePayAccount = PaymentAccounts.some(payment =>
				AccountUtil.hasAccountMigratedToAuctanePay(payment.PaymentAccountId)
			);

			if (isAuctanePayAccount && flags.useAuctanePay) {
				showAuctanePay = true;
			}

			this.setState({
				newValue,
				initialValue: newValue,
				paymentAccounts: PaymentAccounts,
				paymentAccountForChargeCategory: PaymentAccountForChargeCategory,
				associatePayment,
				transactionTypeForChargeCategory: {
					postage: PaymentAccountForChargeCategory.Postage === null,
					serviceFee: PaymentAccountForChargeCategory.ServiceFee === null,
					store: PaymentAccountForChargeCategory.Store === null,
				},
				categoryUsingCorporationPaymentAccount:
					ChargeCategoryUsingCorporationPaymentAccount,
				showAuctanePay,
			});
		} else {
			showError({ response });
		}
	};

	displayLoadingSpinner = (isLoading = true, loadingMessage = '') => {
		this.setState({
			isLoading,
			loadingMessage,
		});
	};

	getPaymentPreview = (paymentAccounts = [], paymentId = '') =>
		paymentAccounts.length > 0
			? paymentAccounts.filter(
					payment => payment.PaymentAccountId === paymentId
				)
			: [];

	getAssociatePayment = (paymentAccounts, paymentForChargeCategory) => {
		const associatePayment = {};
		associatePayment.postage = this.getPaymentPreview(
			paymentAccounts,
			paymentForChargeCategory.Postage
		);
		associatePayment.serviceFee = this.getPaymentPreview(
			paymentAccounts,
			paymentForChargeCategory.ServiceFee
		);
		associatePayment.store = this.getPaymentPreview(
			paymentAccounts,
			paymentForChargeCategory.Store
		);

		return associatePayment;
	};

	associatePaymentAccountForChargeCategory = async () => {
		const {
			initialValue,
			newValue,
			customerId,
			categoryUsingCorporationPaymentAccount,
			categoryUsingOmas,
			categoryUsingInvoicing,
			categoryUsingEps,
			associatePayment,
		} = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		const requestData = { CustId: customerId };

		if (newValue.postage !== initialValue.postage) {
			requestData.Postage =
				categoryUsingCorporationPaymentAccount.Postage ||
				categoryUsingOmas.IsPostageEnabled ||
				categoryUsingInvoicing.IsPostageInvoiced ||
				categoryUsingEps.EPSAccountNumber !== null ||
				associatePayment.postage.length === 0
					? null
					: newValue.postage;
		}

		if (newValue.serviceFee !== initialValue.serviceFee) {
			requestData.ServiceFee =
				categoryUsingCorporationPaymentAccount.Service ||
				categoryUsingInvoicing.IsServiceFeeInvoiced ||
				associatePayment.serviceFee.length === 0
					? null
					: newValue.serviceFee;
		}

		if (newValue.store !== initialValue.store) {
			requestData.Store =
				categoryUsingCorporationPaymentAccount.Store ||
				categoryUsingInvoicing.IsStoreInvoiced ||
				associatePayment.store.length === 0
					? null
					: newValue.store;
		}

		const response =
			await AccountCommunication.associatePaymentAccountForChargeCategory(
				requestData
			);

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				saved: true,
				clean: true,
				initialValue: newValue,
			});

			this.setPaymentFormStateDirty(false);

			// check and udpate if user is updating payment for service fees and user is in resubmit state
			this.checkAndUpdateResubmitState(requestData);
		} else {
			showError({ response });
		}
	};

	associateCorpPaymentAccountForChargeCategory = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const requestData = this.setDataForCorpPaymentAccount();

		const response =
			await AccountCommunication.associateCorporationPaymentAccountForChargeCategory(
				requestData
			);

		this.displayLoadingSpinner(false);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				saved: true,
				clean: true,
			});

			this.setPaymentFormStateDirty(false);

			// check and udpate if user is updating payment for service fees and user is in resubmit state
			this.checkAndUpdateResubmitState(requestData);
		} else {
			showError({ response });
		}
	};

	/**
	 * Check if the credit card user is updateing is tied to the service fees. If yes,
	 * call swsim ResubmitPurchase endpoint when user's account is in resubmit state.
	 * @param {object} request The request data object for /payment/associate/ endpoint.
	 */
	checkAndUpdateResubmitState = async requestData => {
		const {
			modalContext: { showError },
		} = this.props;

		if (requestData.ServiceFee && PaymentUtil.isInResubmitState()) {
			const response = await PaymentUtil.resubmitPurchase();

			this.setState({
				accountInResubmission: PaymentUtil.isInResubmitState(),
			});

			if (!AccountCommunication.isSuccessful(response)) {
				showError({ response });
			}
		}
	};

	setDataForCorpPaymentAccount = () => {
		const { newValue, transactionTypeForChargeCategory } = this.state;

		const data = {
			Postage: newValue.postage,
			ServiceFee: newValue.serviceFee,
			Store: newValue.store,
		};

		Object.keys(data).forEach(val => {
			if (!this.checkIfPaymentExistsMeterLevel(data[val])) {
				delete data[val];
			}
		});

		if (transactionTypeForChargeCategory.postage) {
			data.Postage = null;
		}

		if (transactionTypeForChargeCategory.serviceFee) {
			data.ServiceFee = null;
		}

		if (transactionTypeForChargeCategory.store) {
			data.Store = null;
		}

		return data;
	};

	/**
	 * For handle event change of dropdown
	 */
	onDropdownChange = event => {
		const {
			initialValue,
			newValue,
			paymentAccounts,
			associatePayment,
			isNsaSurchargeEnrolled,
		} = this.state;
		const cloneValue = cloneDeep(newValue);
		const newAssociatePayment = cloneDeep(associatePayment);
		cloneValue[event.target.name] = event.value;
		newAssociatePayment[event.target.name] = this.getPaymentPreview(
			paymentAccounts,
			cloneValue[event.target.name]
		);
		const { PaymentAccountType = 0 } =
			newAssociatePayment[event.target.name]?.[0];
		const paymentCategories = {
			postage: 'Service Fees',
			serviceFee: 'Account Funds',
			store: 'Store Purchases',
		};

		const isAccountFundsChange =
			event.target.name === this.nameDropdowns.accountFunds;
		const isBankAccountSelected = PaymentAccountType === PaymentMethodType.ACH;

		this.setState(
			{
				newValue: cloneValue,
				associatePayment: newAssociatePayment,
				clean: isEqual(initialValue, cloneValue),
				...(isAccountFundsChange && {
					isBankPaymentMethodSelected: isBankAccountSelected,
				}),
			},
			async () => {
				this.savePaymentSettings();

				if (
					isNsaSurchargeEnrolled &&
					isBankAccountSelected &&
					isAccountFundsChange
				) {
					this.showBankAccountSelectedToast();
					this.setState({ isNsaSurchargeEnrolled: false });
				} else {
					await Session.refreshNsaSurcharge();
				}
			}
		);

		if (PaymentAccountType === PaymentMethodType.ACH) {
			Analytics.trackEvent(
				'settings_ach_update',
				{ ach_update: paymentCategories[event.target.name] },
				Analytics.agent.segment
			);
		}
	};

	showBankAccountSelectedToast = () => {
		const { showToast } = this.props;

		showToast({
			toastType: 'success',
			summary:
				'Your bank account is now the payment method assigned to Account Funds.',
			detail: 'You can update your payment methods in settings anytime.',
			position: 'top-right',
			sticky: false,
		});
	};

	showBankAccountRedirectionToast = () => {
		const { showToast } = this.props;

		showToast({
			toastType: 'success',
			summary: 'Have a bank account connected to Stamps?',
			detail:
				'You can avoid credit card fees by assigning your bank account in the Account Funds dropdown in Payment Settings.',
			position: 'top-right',
			sticky: false,
		});
	};

	/**
	 * For handling event changes for the radio button
	 */
	handleRadioButtonChange = e => {
		const { name } = e.target;
		let transactionName;

		if (name.includes(this.nameDropdowns.accountFunds)) {
			transactionName = this.nameDropdowns.accountFunds;
		} else if (name.includes(this.nameDropdowns.serviceFees)) {
			transactionName = this.nameDropdowns.serviceFees;
		} else {
			transactionName = this.nameDropdowns.suppliesPurchases;
		}

		this.setPaymentFormStateDirty(true);

		this.setState(
			prevState => ({
				...prevState,
				transactionTypeForChargeCategory: {
					...prevState.transactionTypeForChargeCategory,
					[transactionName]:
						!prevState.transactionTypeForChargeCategory[transactionName],
				},
				clean: false,
			}),
			() => this.savePaymentSettings()
		);
	};

	getItemTemplate = (params = {}) => {
		const {
			AccountType = '',
			NickName = '',
			AccountNumberDisplayDigits = '',
			PaymentAccountType,
		} = params ?? {};
		let iconType = AccountType.toLowerCase();

		if (PaymentAccountType === PaymentMethodType.ACH) {
			iconType = 'ach';
		}

		return (
			<div className="payment-item">
				<div className="payment-icon table-cell">
					<span className={iconType} />
				</div>
				<div className="payment-title table-cell ellipsis">
					<span>{`${NickName} (${AccountType} *${AccountNumberDisplayDigits})`}</span>
				</div>
			</div>
		);
	};

	renderPaymentField = (dropdownOptions, infoPayments) => {
		const { accountInResubmission } = this.state;
		const isPaymentAccountsEmpty = dropdownOptions.paymentAccounts.length === 0;
		const { planDetails = {} } = Session.getAccountInfo();
		const { isPayAsYouGo } = planDetails;

		const handleSetByAdministrator =
			dropdownOptions.isCorpPaymentAccount ||
			dropdownOptions.isUsingInvoicing ||
			dropdownOptions.isUsingOmas ||
			dropdownOptions.isUsingEps
				? 'Set by Administrator'
				: '';

		const placeholder = isPaymentAccountsEmpty
			? 'No payment methods exist'
			: handleSetByAdministrator;

		const handleDropdownValue =
			dropdownOptions.isCorpPaymentAccount ||
			dropdownOptions.isUsingInvoicing ||
			dropdownOptions.isUsingOmas ||
			dropdownOptions.isUsingEps
				? ''
				: dropdownOptions.value;

		const handleDisableDropdown =
			isPaymentAccountsEmpty ||
			dropdownOptions.isCorpPaymentAccount ||
			dropdownOptions.isUsingInvoicing ||
			dropdownOptions.isUsingOmas ||
			dropdownOptions.isUsingEps;

		const overrideCheck =
			!dropdownOptions.isCorpPaymentAccount &&
			!dropdownOptions.isUsingInvoicing &&
			!dropdownOptions.isUsingOmas &&
			!dropdownOptions.isUsingEps;

		const isServiceFeesPaymentMethod = dropdownOptions.label === 'Service Fees';
		const disableServiceFeesPaymentMethod =
			isServiceFeesPaymentMethod && isPayAsYouGo;

		return (
			<div>
				<WrappedDropdownWithLabel
					className={dropdownOptions.className}
					label={dropdownOptions.label}
					showErrorBelow={dropdownOptions.showErrorBelow}
					uiElementConfig={{
						name: dropdownOptions.name,
						options: dropdownOptions.paymentAccounts,
						onChange: this.onDropdownChange,
						value: handleDropdownValue,
						optionLabel: 'NickName',
						optionValue: 'PaymentAccountId',
						dataqa: dropdownOptions.dataqa,
						disabled: handleDisableDropdown || disableServiceFeesPaymentMethod,
						itemTemplate: this.getItemTemplate,
						valueTemplate: this.getItemTemplate,
						panelClassName: 'payment-dropdown-panel',
						placeholder,
					}}
					errorMessage={errorMessagePayment}
					isInputInvalid={dropdownOptions.inputInvalid === -1}
				/>
				{overrideCheck && (
					<div
						className={`info-payment${
							isServiceFeesPaymentMethod ? '-service-fees' : ''
						}${disableServiceFeesPaymentMethod ? ' disabled-text' : ''}`}
					>
						{isServiceFeesPaymentMethod && accountInResubmission && (
							<p className="resubmission-error-text">
								Your selected payment method is invalid and your service fees
								are past due. Certain account features, including adding funds,
								may be limited until any past due service fees are paid. Please{' '}
								<b>select a valid payment method</b>, and click <b>Save</b>{' '}
								below to reattempt any failed service fee charges.
							</p>
						)}
					</div>
				)}
			</div>
		);
	};

	renderPaymentSettings = () => {
		const { isInWebStore, isInAtgStore } = Session.getSettings();
		const showAllDropdowns = !(isInWebStore || isInAtgStore);

		const {
			paymentAccounts,
			newValue,
			associatePayment,
			categoryUsingInvoicing,
			categoryUsingEps,
			categoryUsingOmas,
			categoryUsingCorporationPaymentAccount,
			validations,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		return (
			<>
				{showAllDropdowns &&
					this.renderPaymentField(
						{
							label: 'Account Funds',
							name: this.nameDropdowns.accountFunds,
							value: newValue.postage,
							showErrorBelow: isMobile,
							dataqa: 'account-funds',
							paymentAccounts,
							isCorpPaymentAccount:
								categoryUsingCorporationPaymentAccount.Postage,
							isUsingInvoicing: categoryUsingInvoicing.IsPostageInvoiced,
							isUsingOmas: categoryUsingOmas.IsPostageEnabled,
							isUsingEps: categoryUsingEps.EPSAccountNumber !== null,
							inputInvalid: validations.postage,
							className: 'payment-dropdown-component',
						},
						associatePayment.postage
					)}
				{showAllDropdowns &&
					this.renderPaymentField(
						{
							label: 'Service Fees',
							name: this.nameDropdowns.serviceFees,
							value: newValue.serviceFee,
							showErrorBelow: isMobile,
							dataqa: 'service-fees',
							paymentAccounts,
							isCorpPaymentAccount:
								categoryUsingCorporationPaymentAccount.Service,
							isUsingInvoicing: categoryUsingInvoicing.IsServiceFeeInvoiced,
							inputInvalid: validations.serviceFee,
							className: 'payment-dropdown-component',
						},
						associatePayment.serviceFee
					)}
				{!Session.canAccessPilotSuppliesStore() &&
					this.renderPaymentField(
						{
							label: 'Supplies Purchases',
							name: this.nameDropdowns.suppliesPurchases,
							value: newValue.store,
							showErrorBelow: isMobile,
							dataqa: 'supplies-purchases',
							paymentAccounts,
							isCorpPaymentAccount:
								categoryUsingCorporationPaymentAccount.Store,
							isUsingInvoicing: categoryUsingInvoicing.IsStoreInvoiced,
							inputInvalid: validations.store,
							className: 'payment-dropdown-component',
						},
						associatePayment.store
					)}
			</>
		);
	};

	renderCorpRadioButtons = radioOptions => {
		const { paymentAccounts, newValue, associatePayment, validations } =
			this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		return (
			<div className="corp-payment-settings">
				<h1 className="corp-radiobutton-label">{radioOptions.label}</h1>
				<div className="radio-buttons">
					<WrappedRadioButton
						inputId={`${radioOptions.name}LocationsSetPaymentRadio`}
						name={`${radioOptions.name}LocationsSetPayment`}
						value={`${radioOptions.name}LocationsSetPayment`}
						onChange={this.handleRadioButtonChange}
						checked={radioOptions.checked}
						className="locationsSetPaymentRadio"
						dataqa={`${radioOptions.name}locationssetpaymentradio`}
						disabled={radioOptions.isCorpPaymentAccount}
					>
						Locations set their own payment method
					</WrappedRadioButton>
					<WrappedRadioButton
						inputId={`${radioOptions.name}PaymentSetForAllLocationsRadio`}
						name={`${radioOptions.name}PaymentSetForAllLocations`}
						value={`${radioOptions.name}PaymentSetForAllLocations`}
						onChange={this.handleRadioButtonChange}
						checked={!radioOptions.checked}
						className="paymentSetForAllLocationsRadio"
						dataqa={`${radioOptions.name}paymentsetforalllocationsradio`}
						disabled={radioOptions.isCorpPaymentAccount}
					>
						Set payment method for all locations
					</WrappedRadioButton>
					{!radioOptions.checked &&
						this.renderPaymentField(
							{
								className: `payment-dropdown-component ${radioOptions.className}`,
								name: radioOptions.name,
								value: newValue[radioOptions.name],
								showErrorBelow: isMobile,
								dataqa: `${radioOptions.className}`,
								paymentAccounts,
								inputInvalid: validations[radioOptions.name],
								isCorpPaymentAccount: radioOptions.isCorpPaymentAccount,
							},
							associatePayment[radioOptions.name]
						)}
				</div>
			</div>
		);
	};

	renderCorpPaymentSettings = () => {
		const {
			transactionTypeForChargeCategory,
			categoryUsingCorporationPaymentAccount,
		} = this.state;
		const { isInWebStore, isInAtgStore } = Session.getSettings();
		const showAllDropdowns = !(isInWebStore || isInAtgStore);

		return (
			<>
				{showAllDropdowns &&
					this.renderCorpRadioButtons({
						label: 'Account Funds:',
						name: this.nameDropdowns.accountFunds,
						checked: transactionTypeForChargeCategory.postage,
						className: 'account-funds',
						isCorpPaymentAccount:
							this.userType === UserRole.RegionAdmin
								? categoryUsingCorporationPaymentAccount.Postage
								: false,
					})}
				{showAllDropdowns &&
					this.renderCorpRadioButtons({
						label: 'Service Fees:',
						name: this.nameDropdowns.serviceFees,
						checked: transactionTypeForChargeCategory.serviceFee,
						className: 'service-fees',
						isCorpPaymentAccount:
							this.userType === UserRole.RegionAdmin
								? categoryUsingCorporationPaymentAccount.Service
								: false,
					})}
				{this.renderCorpRadioButtons({
					label: 'Supplies Purchases:',
					name: this.nameDropdowns.suppliesPurchases,
					checked: transactionTypeForChargeCategory.store,
					className: 'supplies-purchases',
					isCorpPaymentAccount:
						this.userType === UserRole.RegionAdmin
							? categoryUsingCorporationPaymentAccount.Store
							: false,
				})}
			</>
		);
	};

	render() {
		const {
			saved,
			isForCorpPaymentAccount,
			paymentSettingsTitle,
			validations,
			isLoading,
			loadingMessage,
			isNsaSurchargeEnrolled,
			customerId,
			paymentAccounts,
			isBankPaymentMethodSelected,
			isSurchargesMessageDismissed,
		} = this.state;

		const isValidCheck =
			validations.postage === 0 &&
			validations.serviceFee === 0 &&
			validations.store === 0 &&
			saved;

		const shouldShowSurchargeMessage =
			!isLoading &&
			isNsaSurchargeEnrolled &&
			!isBankPaymentMethodSelected &&
			!isSurchargesMessageDismissed;

		return (
			<div className="account-payment payment-settings p-grid">
				<div className="p-col-12">
					<h3 className="account-title">
						{paymentSettingsTitle}&nbsp;
						{isValidCheck && <SaveConfirmation success text="Changes Saved" />}
					</h3>

					{shouldShowSurchargeMessage && (
						<div className="nsa-surcharges-container">
							<NsaSurchargesMessage
								customerId={customerId}
								paymentAccounts={paymentAccounts}
								isForCorpPaymentAccount={isForCorpPaymentAccount}
								callback={this.callback}
								onClose={this.dismissSurchargeMessage}
							/>
						</div>
					)}
					<div className="payment-settings-body">
						{isForCorpPaymentAccount
							? this.renderCorpPaymentSettings()
							: this.renderPaymentSettings()}
						<div className="group-btn-payment">
							<WrappedButton
								buttonType={WrappedButtonType.TertiaryGrey}
								label="Add Payment Method"
								onClick={this.addPayment}
								dataqa="add-payment"
							/>
							<WrappedButton
								buttonType={WrappedButtonType.Secondary}
								label="Manage Payment Methods"
								onClick={this.managePaymentMethods}
								dataqa="manage-payment-methods"
							/>
						</div>
					</div>
				</div>
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

const PaymentSettingsWithLayoutAndModal =
	withLayoutAndModalContexts(PaymentSettings);

export default withLDConsumer()(withToast(PaymentSettingsWithLayoutAndModal));

PaymentSettings.propTypes = {
	// (Optional) Data for a single location when the user is a LocationAdmin
	enterpriseLocation: PropTypes.object,
};
